/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / erreurs axios comme U3106 */
/* eslint-disable react-hooks/exhaustive-deps -- effets alignés chargement id / handlers stables */
import React, { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U3107ROLES } from "../U3107";
import U3107EquipmentBody from "./form/u3107Equipment/U3107EquipmentBody";
import { U3107EquipmentFormToolBar } from "./form/u3107Equipment/U3107EquipmentFormToolBar";
import U3107EquipmentDeleteDlg from "./common/U3107EquipmentDeleteDlg";
import { useU3107Context } from "../U3107Context";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  createEmptyU3107FormRow,
  formRowToApiEquipment,
  luHandleAppiError,
  normalizeU3107EquipmentFromApi,
  type U3107Equipment,
  type U3107EquipmentFormRow,
  type U3107EquipmentSearchRow,
} from "../U3107Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";

function normalizeForForm(row: U3107Equipment): U3107EquipmentFormRow {
  const n = normalizeU3107EquipmentFromApi(row);
  return {
    ...n,
    description: n.description ?? "",
  };
}

export interface U3107EquipmentFormExtendedHandlers extends U2000FormStateHandlersReturn<U3107EquipmentFormRow> {
  addNewEquipment: () => void;
  modifyEquipment: () => void;
  deleteEquipment: () => void;
}

const U3107EquipmentForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3107Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith("fr");
  const listTitle = t("title", { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const validateForm = (row: U3107EquipmentFormRow) => {
    const result: Record<string, string> = {};
    const code = row.code?.trim() ?? "";
    if (!code) {
      result["code"] = t("errCodeRequired", {
        ns: NS,
        defaultValue: isFr ? "Le code est obligatoire." : "Code is required.",
      });
    } else if (code.length < 1 || code.length > 15) {
      result["code"] = t("errCodeLength", {
        ns: NS,
        defaultValue: isFr ? "Le code doit contenir entre 1 et 15 caractères." : "Code must be between 1 and 15 characters.",
      });
    }
    const desc = row.description?.trim() ?? "";
    if (desc === "") {
      result["description"] = t("errDescriptionRequired", {
        ns: NS,
        defaultValue: isFr ? "La description est obligatoire." : "Description is required.",
      });
    } else if (desc.length > 255) {
      result["description"] = t("errDescriptionMax", {
        ns: NS,
        defaultValue: isFr ? "La description ne peut pas dépasser 255 caractères." : "Description cannot exceed 255 characters.",
      });
    }
    const bu = row.billingUnit?.trim() ?? "";
    if (!bu) {
      result["billingUnit"] = t("errBillingUnitRequired", {
        ns: NS,
        defaultValue: isFr ? "L'unité de facturation est obligatoire." : "Billing unit is required.",
      });
    } else if (bu.length > 50) {
      result["billingUnit"] = t("errBillingUnitMax", {
        ns: NS,
        defaultValue: isFr ? "L'unité de facturation ne peut pas dépasser 50 caractères." : "Billing unit cannot exceed 50 characters.",
      });
    }
    if (row.amount === null || row.amount === undefined || !Number.isFinite(row.amount)) {
      result["amount"] = t("errAmountInvalid", {
        ns: NS,
        defaultValue: isFr ? "Le montant doit être un nombre valide." : "Amount must be a valid number.",
      });
    }
    const mf = row.majorFactor;
    if (mf === null || mf === undefined || !Number.isFinite(mf)) {
      result["majorFactor"] = t("errMajorFactorRequired", {
        ns: NS,
        defaultValue: isFr ? "Le facteur de majoration est obligatoire." : "Majoration factor is required.",
      });
    } else if (mf < 0) {
      result["majorFactor"] = t("errMajorFactorInvalid", {
        ns: NS,
        defaultValue: isFr
          ? "Le facteur de majoration doit être un nombre positif ou zéro."
          : "Majoration factor must be zero or a positive number.",
      });
    }
    if (!Number.isFinite(row.active) || !Number.isInteger(row.active)) {
      result["active"] = t("errActiveInvalid", {
        ns: NS,
        defaultValue: isFr ? "L'état actif doit être un entier valide." : "Active flag must be a valid integer.",
      });
    }
    return result;
  };

  const u3107BL: U2000FormSBusinessHandlers<U3107EquipmentFormRow> = {
    newRecord: () => createEmptyU3107FormRow(),

    validateForm,

    saveForme: async (row: U3107EquipmentFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3107FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },

    cancelForm: () => {
      const old = u3107FormExtended.getOldData() as U3107EquipmentFormRow | null;
      if (old) {
        u3107FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === "/U3107") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    onLeaveForm: () => {
      navigate("/U3107");
    },
  };

  const u3107FormHandlers = useU2000FormStateHandlers<U3107EquipmentFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3107BL,
  });

  const u3107FormExtended: U3107EquipmentFormExtendedHandlers = {
    ...u3107FormHandlers,
    addNewEquipment: () => {
      u3107FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyEquipment: () => {
      u3107FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteEquipment: async () => {
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3107EquipmentSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3107EquipmentSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3107Equipment>({
    doGetFn: (rowId: string) => {
      return doGetData(u2000AxiosAuthenticated, rowId);
    },
    onSuccess: (row: U3107Equipment) => {
      const normalized = normalizeForForm(row);
      gridStateHandlers.setDataById(normalized.id, normalized as unknown as U3107EquipmentSearchRow);
      u3107FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const crumbLabel = `${normalized.code}${normalized.description ? ` — ${normalized.description}` : ""}`;
      setLogicalUnitBreadcrumb([
        { href: "/U3107", label: listTitle },
        { label: crumbLabel },
      ]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3107FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3107EquipmentFormRow>({
    doSaveFn: (row: U3107EquipmentFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(formRowToApiEquipment(row)));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3107EquipmentFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeForForm(row as unknown as U3107Equipment);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), normalized as unknown as U3107EquipmentSearchRow]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(row.id, normalized as unknown as U3107EquipmentSearchRow);
      }
      u3107FormExtended.afterSaveData(normalized);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3107") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3107FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3107FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3107FormExtended.getData()],
    doDeleteFn: async (rowId, row) => {
      const r = row as U3107EquipmentFormRow;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(formRowToApiEquipment(r)));
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        toast.success(t("success0003", { key: (del as U3107EquipmentFormRow).code, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3107", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u3107FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3107FormExtended.setData(createEmptyU3107FormRow());
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  if (u3107FormExtended.getData() === null) {
    return null;
  }

  const formTabs = [
    hasRole(U3107ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={u3107FormExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U3107EquipmentAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3107EquipmentDeleteDlg handleDelete={handleDelete as any} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3107EquipmentFormToolBar formStateHandlers={u3107FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3107FormExtended as any}
      >
        <U3107EquipmentBody formStateHandlers={u3107FormExtended as any} t={t} />

        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={formTabs[0]?.id}
            activeTab={activeTab ?? formTabs[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3107EquipmentForm;
